import React, { ComponentType, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { VisibilityControlHelper } from '../helpers/VisibilityControlHelper';
import { ValidationControlHelper } from '../helpers/ValidationControlHelper';
import { FormLogicHelper } from '../helpers/FormLogicHelper';
import _ from 'lodash';
import axios from 'axios'; 
import { ErrorMessageService } from './../../services';
import { getResourceString } from './../../kentico/resource-strings';
import { PopupOk, PopupOkProps } from './../../presentational/PopupOk';
import { ContainedPopupContainer } from './../../containers/PopupContainer';
import { format } from "date-fns";
import { CookieManager } from '@fnv-core/cookies';
import { FormControlMapping } from '../components/FormControlMapping';
import { Step } from '../../presentational/Steps';
import { UserService } from '@fnv-core/authentication';

// Combine state and handlers
export type FormStateProps<TPageModel> = FormState<TPageModel> & FormStateHandlers<TPageModel> & FormStateComponents & {
  handleBlur: () => void,
  handleChange: () => void,
  handleNewFormData: () => void,
  handleNextStep: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void,
  handlePrevStep: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void,
  handleSubmit: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void,
  updateVisibleFields: (stepIndex: number, forceValidation?: boolean) => void,
  initForms: (formDefinition: FormDefinition, userInfoAutoFill?: boolean) => void,
  preloadCustomParams: (params: string[]) => void,
  doAfterSaveActions: (formData: any, pageModel:TPageModel) => void,
  customVisibleConditions: (field: Field, formData: any, formErrors: any, fields: Field[], handleNewFormData: any, isVisible: boolean) => boolean;
  fieldManipulations: (fields: Field[], formData: any) => Field[];
  formatFormDataForSubmit: (key: string, value: any) => any;
  getFormSteps:() => Step[];
};

// Define the HOC
const withFormState = <P extends FormStateProps<TPageModel>, TPageModel>(WrappedComponent: ComponentType<P>) => {
  const WithFormState = forwardRef<any, Omit<P, keyof FormStateProps<TPageModel>>>((state, ref) => {

    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(true);
    const [formSaved, setFormSaved] = useState(false);
    const [onError, setOnError] = useState(false);
    
    const [useDefaultNavigation, setUseDefaultNavigation] = useState(true);
    const [formData, setFormData] = useState({});
    const [pageModel, setPageModel] = useState<TPageModel>((window[`pageModel${(state as any).reactId ?? ""}`] as TPageModel) || null);
    const [initialFormData, setInitialFormData] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [visibleFields, setVisibleFields] = useState([]);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [handleNextFormStep, setHandleNextFormStep] = useState(false);
    const [handlePrevFormStep, setHandlePrevFormStep] = useState(false);
    const [handleFormStepSpecific, setHandleFormStepSpecific] = useState(-1);
    const [handleFormSubmit, setHandleFormSubmit] = useState(false);
    const [formDefinition, setFormDefinition] = useState(null as FormDefinition);
    const [fields, setFields] = useState([] as Field[]);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [showOkPopup, setShowOkPopup] = useState(false);
    
    const formFieldsRef = useRef(null);
    const dateFormat = "dd-MM-yyyy";
    const wordLidBroncode: string = (window["WordLidBroncode"] as string) || null;

    const [fieldManipulations, setFieldManipulations] = useState<(fields: Field[], formData: any) => Field[]>(
      () => { 
        return (fields: Field[], formData: any) => {
          return fields;
        }
      });

    const [doAfterSaveActions, setDoAfterSaveActions] = useState<(formData: any, pageModel: any) => void >(() => { return () => {} });      

    const [customVisibleConditions, setCustomVisibleConditions] = useState<(field: Field, formData: any, formErrors: any, fields: Field[], handleNewFormData: any, isVisible: boolean) => boolean>(
      () => { 
        return (field: Field, formData: any, formErrors: any, fields: Field[], handleNewFormData: any, isVisible: boolean) => {
          return isVisible;
        }
      });
    
    ErrorMessageService.setErrorMessagePropertes(
      getResourceString("Form.Error.Unknown"),
      getResourceString("Form.Error.UnknownMessage"),
      getResourceString("Fnv.PhoneNumber"),
      getResourceString("Fnv.PhoneNumberInfo"),
      getResourceString("Global.Ok"),
      ""
    );

    useEffect(() => {
      if(initialFormData) {
        setFormData((prev) => {
          const newFormErrors = {
            ...prev,
            ...initialFormData
          };
          
          return newFormErrors;
        });
      }
    }, [initialFormData]);

    useEffect(() => {
      if(formDefinition && formData) {
        setFields([...fieldManipulations(formDefinition.form.field, formData)]);
      }
    }, [formData]);

    useEffect(() => {
      updateVisibleFields(currentStepIndex);
    }, [fields]);

    useEffect(() => {
      if(formDefinition && formData) {
        setFields(fieldManipulations(formDefinition.form.field, formData));
      }
    }, [fieldManipulations]);

    useEffect(() => {
      updateVisibleFields(currentStepIndex);
    }, [customVisibleConditions]);

    useEffect(() => {
      if(handleNextFormStep) {
        setHandleNextFormStep(false);
        if(!Object.keys(formErrors).some((key) => visibleFields.some((vField) => vField["@column"] == key) && formErrors[key].length > 0)) {
          const nextStepIndex = Math.min(currentStepIndex + 1, formDefinition.form.sections.length - 1);
          setCurrentStepIndex(nextStepIndex);
          updateVisibleFields(nextStepIndex);
      
          window.dataLayer.push({
            event: "Form steps",
            formName: formDefinition.form.name,
            formStep: (nextStepIndex + 1).toString(),
            formStepName: getResourceString(`Form.Sections.${formDefinition.form.sections[nextStepIndex]["@guid"]}`)
          });
          FormLogicHelper.scrollToTop();
        }
        else {
          FormLogicHelper.scrollToInvalidField(formFieldsRef);
        }
      }
  
      if(handleFormStepSpecific != -1) {
        setCurrentStepIndex(handleFormStepSpecific);
  
        updateVisibleFields(handleFormStepSpecific);
  
        FormLogicHelper.scrollToTop();
  
        setHandleFormStepSpecific(-1);
      }
  
      if(handlePrevFormStep) {
        setHandlePrevFormStep(false);
  
        const nextStepIndex = Math.max(currentStepIndex - 1, 0);
        setCurrentStepIndex(nextStepIndex);
        updateVisibleFields(nextStepIndex);
  
        FormLogicHelper.scrollToTop();
      }
  
      if(handleFormSubmit) {
        setHandleFormSubmit(false);
  
        if(!Object.keys(formErrors).some((key) => visibleFields.some((vField) => vField["@column"] == key) && formErrors[key].length > 0)) {
         
          var bodyFormData = new FormData();
          var entryKeys = Object.keys(formData);
  
          entryKeys.forEach(key => {
            const entry = formData[key];
            entry.value = formatFormDataForSubmit(key, entry.value);
            if(entry.value != null) {
              bodyFormData.append(key, entry.value);
            }
          });

          bodyFormData.append("Invoeromgeving", UserService.getCurrentRole());

          setLoading(true);
  
          axios.put<object>(`/internal/formandcontentreact/set/${formDefinition.form.name}`, bodyFormData, {headers: { "Content-Type": "multipart/form-data" }}).then((response) => {
            try {
              if(response.status == 200) {
                setTimeout(() => {
                  doAfterSaveActions(formData, pageModel);
                  handleFormAfterSave();
                }, 1500);
              }
              else {
                setLoading(false);
                setOnError(true);
              }
            } catch (error) {
              setLoading(false);
              setOnError(true);
            }
          });
        }
        else {
          FormLogicHelper.scrollToInvalidField(formFieldsRef);
        }
      }
  
    }, [handleNextFormStep, handlePrevFormStep, handleFormStepSpecific, handleFormSubmit]);

    const handleFormAfterSave = () : void => {
      if((pageModel as any)?.successUrl != null && (pageModel as any).successUrl != "") {
        CookieManager.createCookie("submitted-form", formDefinition.form.name, 1);
        window.location.replace(decodeURIComponent((pageModel as any).successUrl));
        return;
      }
      else if(formDefinition.form.afterSave.redirectUrl) {
        CookieManager.createCookie("submitted-form", formDefinition.form.name, 1);
        window.location.replace(formDefinition.form.afterSave.redirectUrl);
        return;
      }

      setLoading(false);
      setShowOkPopup(true);
    };

    const onPopupOKSubmit = () => {
      if(formDefinition.form.afterSave.displayText) {
        window.location.replace(window.location.origin);
        return;
      }

      window.location.reload();
    }

    const formatFormDataForSubmit = (key: string, value: any) : any => {
      var field = fields.find(f => f["@column"] === key);
  
      if(field == null || value == null) {
        return null;
      }
  
      var controlType = field.settings.componentidentifier;
      switch (controlType) {
        case "ConsentAgreement":
          if(!value || (typeof value !== "boolean")) {
            return null;
          }
          break;
        case "CalendarControl":
          return format(new Date(value), dateFormat);
        default:
          break;
      }
  
      return value;
    };

    const preloadQueryParams = (formDefinitions: FormDefinition) : void => {

      // Get the query params from the URL
      const queryParams = FormLogicHelper.getQueryParams();

      // Initialize form data with query parameters for hidden fields or default values
      const initialData = {};
      formDefinitions.form.field.forEach(field => {
        if(field.settings.IsHiddenField == "True") {
          if(field["@column"].toLowerCase() == 'broncode' && !queryParams[field["@column"].toLowerCase()]) {
            initialData[field["@column"]] = {value: wordLidBroncode, enabled: false, touched: false, focus: false, loading: false} as FormDataEntry;
          }
          else if(queryParams[field["@column"].toLowerCase()]) {
            initialData[field["@column"]] = {
              value: FormControlMapping.GetComponentTypedValue(queryParams[field["@column"].toLowerCase()], field.settings.componentidentifier), 
              enabled: false, 
              touched: false, 
              focus: false, 
              loading: false
            } as FormDataEntry;
          }
          else if(field.properties.defaultvalue && initialData[field["@column"]] == null) {
            initialData[field["@column"]] = {
              value: FormControlMapping.GetComponentTypedValue(field.properties.defaultvalue, field.settings.componentidentifier), 
              enabled: false, 
              touched: false, 
              focus: false, 
              loading: false
            } as FormDataEntry;
          }
        }
        else {
          if(queryParams[field["@column"].toLowerCase()]) {
            field.properties.defaultvalue = queryParams[field["@column"].toLowerCase()];
          }
        }
      });

      setInitialFormData(initialData);
    }

    const initForms = (formDefinitions: FormDefinition, userInfoAutoFill: boolean = true) => {
      // prefill
      try {

        const prefillPromise = userInfoAutoFill ? axios.get<object>(`/internal/formandcontentreact/get/${formDefinitions.form.name.toLowerCase()}`).then((response) => {
          if(response.status == 200) {
            const dataKeys = Object.keys(response.data);
            for (let index = 0; index < dataKeys.length; index++) {
              const key = dataKeys[index];
              const data = response.data[key];

              if(FormLogicHelper.isDefaultValueFromApi(data)) {
                continue;
              }
              
              const indexOfField = formDefinitions.form.field.findIndex((formField) => formField["@column"].toLowerCase() == key.toLowerCase());
              if(indexOfField > -1) {
                const field = formDefinitions.form.field[indexOfField];
                field.properties.defaultvalue = response.data[key];
                formDefinitions.form.field[indexOfField] = field;
              }
            }
          }

          return new Promise<void>((resolve) => { resolve();});
        }) : new Promise<void>((resolve) => { resolve();});
        
        prefillPromise.then(() => {
          setFormDefinition(formDefinitions);
          preloadQueryParams(formDefinitions);
        });
        
      } catch (error) {
        console.log(error);
        setFormLoading(false);
        setOnError(true);
      }

      window.addEventListener('resize', handleResize);
      
      // Cleanup the event listener when the component unmounts
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    const handleBlur = (e) => {
      const { name } = e.target;
      handleNewFormData(name, undefined, false, true, true);
    };
  
    const handleChange = (e) => {
      const { name, value } = e.target;
      handleNewFormData(name, value, true, true, true);
      if(formData[name]?.value?.length ?? 0 <= (value?.length ?? 0 - 1)) {
        handleBlur(e);
      }
    };

    const handleNewFormData = (name: string, value: any, focus: boolean = false, touched: boolean = false, enabled: boolean = true) => {
      setFormData((prevFormData) => {
        let formDataEntry: FormDataEntry = prevFormData[name] || { value: undefined, focus: undefined, touched: undefined, enabled: undefined };
  
        // Update the formDataEntry properties
        if(value !== undefined) {
          formDataEntry.value = value;
        }
        formDataEntry.focus = focus;
        formDataEntry.touched = touched;  
        formDataEntry.enabled = enabled;

        const newFormData = {
          ...prevFormData,
          [name]: formDataEntry,
        };
        
        return newFormData;
      });
    };

    const handleNextStep = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {

      event.preventDefault();
  
      updateVisibleFields(currentStepIndex, true);
  
      setHandleNextFormStep(true);
    };
    
    const handlePrevStep = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
  
      event.preventDefault();
  
      setHandlePrevFormStep(true);
    };
  
    const handleSubmit = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      event.preventDefault();
  
      updateVisibleFields(currentStepIndex, true);
  
      setHandleFormSubmit(true);
    };

    // Function to update visible fields based on current form data
    const updateVisibleFields = (stepIndex: number, forceValidation: boolean = false) => {
      const updatedVisibleFields = [];
      if(fields) {
        fields.forEach(field => {
          if(formDefinition.form.sections[stepIndex].fields.some((stepField) => stepField.identifier == field["@guid"])) {
            if(field.settings.IsHiddenField !== "True") {
              if (VisibilityControlHelper.isFieldVisible(field, formData, formErrors, fields, handleNewFormData, customVisibleConditions)) {
                updatedVisibleFields.push(field);
                if(field.settings.componentidentifier == "FormFieldGroup" || field.settings.componentidentifier == "GridControl") {
                  return;
                }
                const errors = ValidationControlHelper.validateField(fields, field["@column"], formData, setFormData, handleNewFormData, !field["@allowempty"], forceValidation);
                addErrorsToFormErrors(field["@column"], errors);
              } else {
                // Reset the value of the field if it is not visible
                delete formData[field["@column"]];
                delete formErrors[field["@column"]];
              }
            }
          }
        });
      }

      setVisibleFields(updatedVisibleFields);
    };

    const addErrorsToFormErrors = (name, errors) => {
      if (_.isEqual(errors, formErrors[name])) {
        return;
      }
      setFormErrors((prevFormErrors) => {
        const newFormErrors = {
          ...prevFormErrors,
          [name]: errors,
        };
        
        return newFormErrors;
      });
    };

    const getFormSteps = () => {
      if(!formDefinition || !fields) {
        return [];
      }

      const steps = formDefinition.form.sections.map((section, index) => {

        let sectionTitle = getResourceString(`Form.Sections.${section["@guid"]}`);
        if(sectionTitle == "") {
          const sectionFields = section.fields.map(field => {
            return fields.find(f => f["@guid"] === field.identifier);
          })?.filter(Boolean) ?? [];

          const firstFormFieldGroup = sectionFields.find(item => item.settings.componentidentifier === "FormFieldGroup");
          sectionTitle = firstFormFieldGroup?.properties?.fieldcaption;
        }

        return { 
          title: sectionTitle ?? `Form.Sections.${section["@guid"]}`, 
          formName: index.toString()
        }; 
      });

      return steps;
    };

    const FormNavigationBar = ({}) => {
      return (
        <>
          {formDefinition.form.sections.length > 1 && useDefaultNavigation && (
            <div className="row no-gutters mt-3">
            {(() => {
              if(currentStepIndex !== 0){
                return (  
                  <div className="col-auto form__vorig-stap-col">
                    <a className="form__vorig-stap" href="#" onClick={handlePrevStep}><span className="icon icon-open-chevron-left"></span>{getResourceString("Form.Step.Previous")}</a>
                  </div>
                );
              }
            })()}
            <div className="col d-flex justify-content-end form__button-next-step">
              {(() => {
                if(currentStepIndex !== formDefinition.form.sections.length - 1){
                  return (  
                    <button type="button" className="btn btn-success" onClick={handleNextStep}>{getResourceString("Form.Step.Next")}<span className="icon icon-open-chevron-right"></span></button>
                  );
                }
                else {
                  return (
                    <button type="submit" className="btn btn-success btn-form-submit" onClick={handleSubmit}>{getResourceString("Form.Send.ButtonCaption")}</button>
                  );
                }
              })()}
            </div>
          </div>
          )}
        </>
      )
    }

    // Expose methods via ref if needed
    useImperativeHandle(ref, () => ({
      setLoading,
      setFormLoading,
      setFormSaved,
      setOnError,
      setShowOkPopup,
      setFormData,
      setUseDefaultNavigation,
      setFormErrors,
      setVisibleFields,
      setCurrentStepIndex,
      setHandleNextFormStep,
      setHandlePrevFormStep,
      setHandleFormStepSpecific,
      setHandleFormSubmit,
      setFormDefinition,
      setWindowWidth,
      handleBlur,
      handleChange,
      handleNewFormData,
      handleNextStep,
      handlePrevStep,
      handleSubmit,
      updateVisibleFields,
      initForms
    }));

    return (
      <>
        {onError && (
          <ContainedPopupContainer
            forms={{ formGetApiError: true }}
            setFormValues={null}
          />
        )}

        {!formLoading && (
          <PopupOk
            {...({
              onPopupOK:
                (state as any).props?.onPopupOkClick != null
                  ? (state as any).props?.onPopupOkClick
                  : onPopupOKSubmit,
              okButtonText:
                getResourceString("Global.OK") != ""
                  ? getResourceString("Global.OK")
                  : "OK",
              withIcon: null,
              message:
                (state as any).props?.onSuccessPopupMessage != ""
                  ? (state as any).props?.onSuccessPopupMessage
                  : formDefinition.form.afterSave.displayText
                  ? formDefinition.form.afterSave.displayText
                  : getResourceString("Form.Popup.SuccessfullySend") ||
                    "Succesvol verstuurd",
              description:
                (state as any).props?.onSuccessPopupDescription != ""
                  ? (state as any).props?.onSuccessPopupDescription
                  : "",
              hideModal: !showOkPopup,
            } as PopupOkProps)}
          />
        )}

        <WrappedComponent
          {...(state as P)}
          // FormStateProps
          pageModel={pageModel}
          formFieldsRef={formFieldsRef}
          formLoading={formLoading}
          formSaved={formSaved}
          loading={loading}
          showOkPopup={showOkPopup}
          useDefaultNavigation={useDefaultNavigation}
          windowWidth={windowWidth}
          onError={onError}
          formData={formData}
          formErrors={formErrors}
          visibleFields={visibleFields}
          currentStepIndex={currentStepIndex}
          handleNextFormStep={handleNextFormStep}
          handlePrevFormStep={handlePrevFormStep}
          handleFormStepSpecific={handleFormStepSpecific}
          handleFormSubmit={handleFormSubmit}
          formDefinition={formDefinition}
          fields={fields}
          // FormStateHandlers
          setPageModel={setPageModel}
          setFormLoading={setFormLoading}
          setFormSaved={setFormSaved}
          setLoading={setLoading}
          setShowOkPopup={setShowOkPopup}
          setUseDefaultNavigation={setUseDefaultNavigation}
          setWindowWidth={setWindowWidth}
          setOnError={setOnError}
          setFormData={setFormData}
          setFormErrors={setFormErrors}
          setVisibleFields={setVisibleFields}
          setCurrentStepIndex={setCurrentStepIndex}
          setHandleNextFormStep={setHandleNextFormStep}
          setHandlePrevFormStep={setHandlePrevFormStep}
          setHandleFormStepSpecific={setHandleFormStepSpecific}
          setHandleFormSubmit={setHandleFormSubmit}
          setFields={setFields}
          setFormDefinition={setFormDefinition}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleNewFormData={handleNewFormData}
          updateVisibleFields={updateVisibleFields}
          handleNextStep={handleNextStep}
          handlePrevStep={handlePrevStep}
          handleSubmit={handleSubmit}
          initForms={initForms}
          doAfterSaveActions={doAfterSaveActions}
          setDoAfterSaveActions={setDoAfterSaveActions}
          customVisibleConditions={customVisibleConditions}
          setCustomVisibleConditions={setCustomVisibleConditions}
          fieldManipulations={fieldManipulations}
          setFieldManipulations={setFieldManipulations}
          formatFormDataForSubmit={formatFormDataForSubmit}
          getFormSteps={getFormSteps}
          // FormStateComponents
          FormNavigationBar={FormNavigationBar}
        />
      </>
    );
  });

  return WithFormState;
};

export default withFormState;